import React from 'react';
import cx from 'classnames';
import { renderToString } from 'react-dom/server';
import { isEmpty, isNil, last } from 'lodash-es';

import lineChartConfig from '../../../../components/Chart/LineChart';
import { formatQuarterYear } from '../../../../utils/date';
import { getFallbackSubDateByGranularity } from '../../../../utils/helpers';
import { numberToHumanChart } from '../../../../utils/formatter';

const onNextTick = (callback, timeout = 0) => setTimeout(callback, timeout);

export const getComparisonDatePair = (datePair, {
  currentTimestamp,
  overPeriod,
  granularity = 'quarterly',
}) => {
  if (!isEmpty(datePair)) {
    return {
      currDate: datePair.currQuarter,
      prevDate: datePair.prevQuarter,
    };
  }

  // Fallback
  const subDate = getFallbackSubDateByGranularity({
    isOverPeriod: overPeriod === 'period_over_period',
    granularity,
    date: currentTimestamp,
  });

  return {
    currDate: formatQuarterYear(currentTimestamp),
    prevDate: formatQuarterYear(subDate),
  };
};

export const baseDriversConfig = (seriesByMetric, {
  chartData,
  overPeriod,
  chartStyleOverride,
}) => {
  const animation = chartStyleOverride?.animation ?? {
    duration: 300,
  };

  const { partial_days, sub_dates_grouped } = chartData;

  const startPartialDate = 1;
  const endPartialDate = partial_days || startPartialDate;

  const xTickPositions = seriesByMetric.map(value => value[0]);

  return lineChartConfig({
    chartOptions: {
      type: 'column',
      spacing: [10, 0, 8, 0],
      height: chartStyleOverride?.chartHeight || 225,
      animation,
    },
    yAxis: {
      title: null,
      tickAmount: 6,
      startOnTick: true,
      labels: {
        reserveSpace: true,
        y: 5,
        style: {
          color: '#848EA1',
          fontSize: chartStyleOverride?.axisLabelsFontSize || 12,
        },
        formatter() {
          return `${this.value}%`;
        },
      },
      gridLineWidth: 1,
      gridLineColor: '#EDF0F5',
    },
    xAxisOptions: {
      tickLength: 0,
      lineWidth: 0,
      crosshair: false,
      minPadding: 0,
      tickPositions: xTickPositions.length > 0 ? xTickPositions : null,
    },
    xAxisLabels: {
      formatter() {
        return `<span style="${this.isLast && 'fill: #348BE3; font-weight: 700;'}">${sub_dates_grouped?.[this.value]?.currQuarter || formatQuarterYear(this.value)}</span>`;
      },
      style: {
        color: '#848EA1',
        fontSize: 12,
      },
      showFirstLabel: true,
      autoRotation: true,
      y: 26,
    },
    tooltipOptions: {
      backgroundColor: 'transparent',
      borderWidth: 0,
      borderRadius: 10,
      padding: 0,
      useHTML: true,
      outside: true,
      formatter() {
        const isPartialElem = this.points[0].point.options.isPartial;

        const { currDate, prevDate } = getComparisonDatePair(sub_dates_grouped?.[this.x], {
          currentTimestamp: this.x,
          overPeriod,
        });

        return renderToString(
          <div
            style={{
              minWidth: '170px',
              borderRadius: '8px',
              border: '1px solid #E1E5EB',
              padding: '12px 16px',
              backgroundColor: '#fff',
              boxShadow: '0px 0px 24px 0px rgba(0, 0, 0, 0.08)',
            }}
          >
            <table className="table">
              <tr style={{ display: !isPartialElem && 'none' }}>
                <td className="text-left text-black text-12 p-l-0 p-b-0 pr-md">
                  Days {endPartialDate === 0 ? `${startPartialDate}` : `${startPartialDate} - ${endPartialDate}`}
                </td>
              </tr>
              <tr>
                <td className="text-left text-black text-12 p-l-0 p-b-0 pr-md">
                  {currDate} vs {prevDate}
                </td>
                <td className="text-right  text-12 p-l-0 p-b-0" style={{ color: '#5D6B85', lineHeight: '150%' }}>
                  <div className="text-bold text-black text-16">
                    <span>{this.y}%</span>
                  </div>
                </td>
              </tr>
            </table>
          </div>,
        );
      },
    },
    plotOptionsSeries: {
      animation,
    },
    plotOptionsSettings: {
      column: {
        minPointLength: 5,
        groupPadding: 0.05,
        borderRadius: 0,
      },
    },
  });
};

export const advancedColumnDriversConfig = (seriesByMetric, {
  chartData,
  quarterDatesByTimestamp,
  metric,
  activeTimestamp,
  overPeriod,
  chartStyleOverride,
  renderActiveRect,
  renderHoverRect,
  renderMaskRects,
  onPointSelect,
}) => {
  const animation = chartStyleOverride?.animation ?? {
    duration: 300,
  };

  const { partial_days, sub_dates_grouped } = chartData;

  const startPartialDate = 1;
  const endPartialDate = partial_days || startPartialDate;

  const xTickPositions = seriesByMetric.map(value => value[0]);

  const getRectParams = ({
    chartHeight,
    chartWidth,
    plotWidth,
    points,
    targetPointX,
  }) => {
    const getRectParamsByPoint = ({ pointWidth, barX }) => {
      const leftOffset = chartWidth - plotWidth;
      const padding = 7;
      const plotBandsWidth = pointWidth + 2 * padding;
      const left = (barX + leftOffset) - padding;

      return {
        height: chartHeight - 2,
        width: plotBandsWidth,
        top: 1,
        left,
      };
    };

    if (points) {
      if (isNil(targetPointX)) {
        return points.map(point => getRectParamsByPoint(point));
      }

      const activePoint = points.find(point => point.x === targetPointX) || last(points);

      if (activePoint) {
        return getRectParamsByPoint(activePoint);
      }
    }

    return null;
  };

  let destroyHoverRect;
  let activeRectLeftCoord;

  const clearHoverRect = () => {
    if (destroyHoverRect) {
      destroyHoverRect();
      destroyHoverRect = undefined;
    }
  };

  return lineChartConfig({
    chartOptions: {
      type: 'column',
      spacing: [10, 0, 8, 0],
      height: 225,
      animation,
      events: {
        load() {
          // Reset coords
          activeRectLeftCoord = undefined;
        },
        render(event) {
          const { chartHeight, chartWidth, plotWidth, series } = event.target;
          const { points } = series[0] || {};

          if (!points) return;
          const chartRenderer = this.renderer;

          // Render active rect
          const activeRectParams = getRectParams({
            chartHeight,
            chartWidth,
            plotWidth,
            points,
            targetPointX: activeTimestamp,
          });

          // Is active point changed
          if (activeRectParams && (activeRectParams.left !== activeRectLeftCoord)) {
            activeRectLeftCoord = activeRectParams.left;
            renderActiveRect(chartRenderer, [activeRectParams]);

            // Render mask rects
            const maskRectsParams = getRectParams({
              chartHeight,
              chartWidth,
              plotWidth,
              points,
            });

            renderMaskRects(chartRenderer, maskRectsParams, points.map((point, index) => ({
              className: cx('rect-mask-overlay', point.isActivePoint && 'active'),
              onClick: () => {
                clearHoverRect();

                if (onPointSelect && !point.isActivePoint) {
                  onPointSelect({
                    quarterLabel: quarterDatesByTimestamp[point.x],
                    metric,
                  });
                }
              },
              mouseOver() {
                if (point.isActivePoint) {
                  clearHoverRect();
                  return;
                }
                const rectParams = maskRectsParams[index];

                if (!rectParams) return;

                destroyHoverRect = renderHoverRect(chartRenderer, [rectParams]);
              },
              mouseOut() {
                if (point.isActivePoint) return;
                clearHoverRect();
              },
            })));
          }
        },
      },
    },
    yAxis: {
      title: null,
      tickAmount: 5,
      startOnTick: true,
      labels: {
        reserveSpace: true,
        y: 5,
        style: {
          color: '#848EA1',
          fontSize: 12,
        },
        formatter() {
          return `${this.value}%`;
        },
      },
      gridLineWidth: 1,
      gridLineColor: '#EDF0F5',
    },
    xAxisOptions: {
      tickLength: 0,
      lineWidth: 0,
      crosshair: false,
      minPadding: 0,
      tickPositions: xTickPositions.length > 0 ? xTickPositions : null,
    },
    xAxisLabels: {
      formatter() {
        return `<span style="${this.value === activeTimestamp && 'fill: #348BE3; font-weight: 700;'}">${sub_dates_grouped?.[this.value]?.currQuarter || formatQuarterYear(this.value)}</span>`;
      },
      style: {
        color: '#848EA1',
        fontSize: 12,
      },
      showFirstLabel: true,
      autoRotation: true,
      y: 26,
      zIndex: 2,
    },
    tooltipOptions: {
      backgroundColor: 'transparent',
      borderWidth: 0,
      borderRadius: 10,
      padding: 0,
      useHTML: true,
      outside: true,
      formatter() {
        const isPartialElem = this.points[0].point.options.isPartial;

        const { currDate, prevDate } = getComparisonDatePair(sub_dates_grouped?.[this.x], {
          currentTimestamp: this.x,
          overPeriod,
        });

        return renderToString(
          <div
            style={{
              minWidth: '170px',
              borderRadius: '8px',
              border: '1px solid #E1E5EB',
              padding: '12px 16px',
              backgroundColor: '#fff',
              boxShadow: '0px 0px 24px 0px rgba(0, 0, 0, 0.08)',
            }}
          >
            <table className="table">
              <tr style={{ display: !isPartialElem && 'none' }}>
                <td className="text-left text-black text-12 p-l-0 p-b-0 pr-md">
                  Days {endPartialDate === 0 ? `${startPartialDate}` : `${startPartialDate} - ${endPartialDate}`}
                </td>
              </tr>
              <tr>
                <td className="text-left text-black text-12 p-l-0 p-b-0 pr-md">
                  {currDate} vs {prevDate}
                </td>
                <td className="text-right  text-12 p-l-0 p-b-0" style={{ color: '#5D6B85', lineHeight: '150%' }}>
                  <div className="text-bold text-black text-16">
                    <span>{this.y}%</span>
                  </div>
                </td>
              </tr>
            </table>
          </div>,
        );
      },
    },
    plotOptionsSeries: {
      animation,
    },
    plotOptionsSettings: {
      column: {
        minPointLength: 5,
        groupPadding: 0.1,
        borderRadius: 0,
        borderWidth: 0,
      },
    },
  });
};

const decorateYValue = (value, postfix = '%') => value && `${numberToHumanChart(value)}${postfix}`;

export const advancedLineDriversConfig = ({
  floor,
  ceiling,
  labelStep,
  chartStyleOverride,
  setHintCoords,
  metric,
}) => {
  const animation = chartStyleOverride?.animation ?? {
    duration: 300,
  };

  return lineChartConfig({
    chartOptions: {
      type: 'spline',
      spacing: [10, 0, 8, 0],
      height: chartStyleOverride?.height || 225,
      animation,
      backgroundColor: 'transparent',
      events: {
        load() {
          if (setHintCoords) {
            // Assume that there is only one dataLabel (Hint icon dataLabel)
            const targetSeries = this.series.find(({ dataLabelsGroup }) => dataLabelsGroup);
            if (!targetSeries) return;
            const targetPoint = targetSeries.points.find(({ dataLabel }) => dataLabel);
            if (!targetPoint) return;
            // To ensure that absoluteBox is calculated
            onNextTick(() => setHintCoords(targetPoint.dataLabel.absoluteBox));
          }
        },
      },
    },
    yAxis: {
      title: null,
      floor,
      ceiling,
      minPadding: 0.01,
      maxPadding: 0.01,
      startOnTick: true,
      tickAmount: 5,
      gridLineColor: '#EDF0F5',
      labels: {
        reserveSpace: true,
        style: {
          color: '#848EA1',
          fontSize: 12,
        },
        formatter() {
          return decorateYValue(this.value, metric === 'retention' ? '%' : '');
        },
      },
    },
    xAxisOptions: {
      crosshair: false,
      lineWidth: 0,
      tickLength: 0,
      type: 'category',
    },
    xAxisLabels: {
      formatter() {
        return this.value;
      },
      style: {
        color: '#848EA1',
        fontSize: 12,
      },
      y: 26,
      showFirstLabel: true,
      step: labelStep,
    },
    tooltipOptions: {
      backgroundColor: 'transparent',
      borderWidth: 0,
      borderRadius: 10,
      padding: 0,
      useHTML: true,
      outside: true,
      formatter() {
        let rows;

        const template = (point, index) => (
          <tr key={index}>
            <td className="text-left text-black text-14 p-l-0 p-b-0 pr-md">
              <div className="text-black text-14 d-flex" style={{ maxWidth: '100%' }}>
                <span
                  className="tooltip-bubble-marker mt-xxxs"
                  style={{
                    backgroundColor: point.color,
                    borderColor: point.color,
                  }}
                />
                <span>{point.point.tooltipPointLabel}</span>
              </div>
            </td>
            <td className="text-right text-black text-14 p-l-0 p-b-0">
              {decorateYValue(point.y, metric === 'retention' ? '%' : ' minutes')}
            </td>
          </tr>
        );

        if (Array.isArray(this.points)) {
          rows = this.points.map((point, index) => template(point, index));
        } else return false;

        const tooltipHeader = null;

        return renderToString(
          <div
            style={{
              minWidth: '189px',
              borderRadius: '8px',
              border: '1px solid #E1E5EB',
              padding: '16px',
              backgroundColor: '#fff',
              boxShadow: '0px 0px 24px 0px rgba(0, 0, 0, 0.08)',
            }}
          >
            {tooltipHeader && (
              <div
                style={{
                  fontSize: '12px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '150%',
                  color: '#848EA1',
                  marginBottom: '8px',
                }}
              >
                {tooltipHeader}
              </div>
            )}
            <table className="table">{rows}</table>
          </div>,
        );
      },
    },
    plotOptionsSeries: {
      animation,
    },
  });
};
